import Cookies from 'js-cookie';

/**
 * Cookie service for managing cookies in the application
 */
class CookieService {
  /**
   * Get the expiry date for cookies based on environment variable
   * @returns {number} Number of days until cookie expires
   */
  static getExpiryDays() {
    return parseInt(process.env.VUE_APP_COOKIE_EXPIRY_DAYS || 365, 10);
  }

  /**
   * Get the name of the analytics cookie from environment variable
   * @returns {string} Name of the analytics cookie
   */
  static getAnalyticsCookieName() {
    return process.env.VUE_APP_ANALYTICS_COOKIE_NAME || 'analyticsCookies';
  }

  /**
   * Get the name of the required cookie from environment variable
   * @returns {string} Name of the required cookie
   */
  static getRequiredCookieName() {
    return process.env.VUE_APP_REQUIRED_COOKIE_NAME || 'requiredCookies';
  }

  /**
   * Get the name of the third-party cookie from environment variable
   * @returns {string} Name of the third-party cookie
   */
  static getThirdPartyCookieName() {
    return process.env.VUE_APP_THIRD_PARTY_COOKIE_NAME || 'thirdPartyCookies';
  }

  /**
   * Set a cookie with the specified name and value
   * @param {string} name - Name of the cookie
   * @param {any} value - Value to set
   * @param {Object} options - Additional options for the cookie
   */
  static setCookie(name, value, options = {}) {
    const defaultOptions = {
      expires: this.getExpiryDays(),
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'Lax'
    };
    
    Cookies.set(name, value, { ...defaultOptions, ...options });
  }

  /**
   * Get a cookie by name
   * @param {string} name - Name of the cookie to retrieve
   * @returns {any} Value of the cookie or undefined if not found
   */
  static getCookie(name) {
    return Cookies.get(name);
  }

  /**
   * Remove a cookie by name
   * @param {string} name - Name of the cookie to remove
   */
  static removeCookie(name) {
    Cookies.remove(name);
  }

  /**
   * Check if analytics cookies are accepted
   * @returns {boolean} True if analytics cookies are accepted
   */
  static isAnalyticsAccepted() {
    return this.getCookie(this.getAnalyticsCookieName()) === 'true';
  }

  /**
   * Check if required cookies are accepted
   * @returns {boolean} True if required cookies are accepted
   */
  static isRequiredAccepted() {
    return this.getCookie(this.getRequiredCookieName()) === 'true';
  }

  /**
   * Check if third-party cookies are accepted
   * @returns {boolean} True if third-party cookies are accepted
   */
  static isThirdPartyAccepted() {
    return this.getCookie(this.getThirdPartyCookieName()) === 'true';
  }

  /**
   * Accept all cookies
   */
  static acceptAllCookies() {
    this.setCookie(this.getRequiredCookieName(), true);
    this.setCookie(this.getAnalyticsCookieName(), true);
    this.setCookie(this.getThirdPartyCookieName(), true);
  }

  /**
   * Accept specific cookies based on user preferences
   * @param {Object} preferences - User cookie preferences
   */
  static acceptCookies(preferences) {
    this.setCookie(this.getRequiredCookieName(), true);
    this.setCookie(this.getAnalyticsCookieName(), preferences.analyticsCookies);
    this.setCookie(this.getThirdPartyCookieName(), preferences.thirdPartyCookies);
  }

  /**
   * Initialize analytics services based on cookie preferences
   */
  static initializeAnalytics() {
    if (this.isAnalyticsAccepted()) {
      // Initialize Google Analytics if ID is provided
      if (process.env.VUE_APP_GOOGLE_ANALYTICS_ID) {
        this.initializeGoogleAnalytics();
      }
      
      // Initialize Facebook Pixel if ID is provided
      if (process.env.VUE_APP_FACEBOOK_PIXEL_ID) {
        this.initializeFacebookPixel();
      }
      
      // Initialize Hotjar if ID is provided
      if (process.env.VUE_APP_HOTJAR_ID) {
        this.initializeHotjar();
      }
    }
  }

  /**
   * Initialize Google Analytics
   */
  static initializeGoogleAnalytics() {
    // Google Analytics initialization code
    // This is a placeholder - you would need to implement the actual GA code
    console.log('Google Analytics initialized with ID:', process.env.VUE_APP_GOOGLE_ANALYTICS_ID);
  }

  /**
   * Initialize Facebook Pixel
   */
  static initializeFacebookPixel() {
    // Facebook Pixel initialization code
    // This is a placeholder - you would need to implement the actual FB Pixel code
    console.log('Facebook Pixel initialized with ID:', process.env.VUE_APP_FACEBOOK_PIXEL_ID);
  }

  /**
   * Initialize Hotjar
   */
  static initializeHotjar() {
    // Hotjar initialization code
    // This is a placeholder - you would need to implement the actual Hotjar code
    console.log('Hotjar initialized with ID:', process.env.VUE_APP_HOTJAR_ID);
  }
}

export default CookieService; 
